import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Table,
  Space,
  Tag,
  Badge,
  Descriptions,
  Divider,
  Button,
  message,
  Typography,
  Form,
  Input,
  Card,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  getTransactions,
  getTransactionById,
  updateTransactionStatus,
} from "../../services/transactionService";
import { formatCurrency, formatDate } from "../../utils/formatters";

const { Title, Text } = Typography;

const UserTransactionsModal = ({
  visible,
  onClose,
  userId = null,
  profileId = null,
  userName = "",
}) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [declineForm] = Form.useForm();
  const scrollContainerRef = useRef(null);

  // Fetch transactions for the user or business
  const fetchTransactions = useCallback(async (cursor = null, isLoadMore = false) => {
    if (!visible || (!userId && !profileId)) return;

    try {
      if (!isLoadMore) {
        setLoading(true);
      }

      // Build filter params
      const params = {
        limit: 15,
      };
      
      if (userId) {
        params.user_id = userId;
      }
      if (profileId) {
        params.profile_id = profileId;
      }
      if (cursor) {
        params.next_cursor = cursor;
      }

      const response = await getTransactions(params);
      const newTransactions = response.transactions || [];
      
      if (isLoadMore) {
        setTransactions(prev => [...prev, ...newTransactions]);
      } else {
        setTransactions(newTransactions);
      }
      
      setNextCursor(response.next_cursor || null);
      setHasMore(!!response.next_cursor);
      
    } catch (error) {
      console.error("Error fetching user transactions:", error);
      message.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [visible, userId, profileId]);

  // Load more transactions
  const loadMoreTransactions = useCallback(() => {
    if (!loading && hasMore && nextCursor) {
      fetchTransactions(nextCursor, true);
    }
  }, [fetchTransactions, loading, hasMore, nextCursor]);

  // Handle scroll to load more
  const handleScroll = useCallback((e) => {
    const { target } = e;
    const { scrollTop, scrollHeight, clientHeight } = target;
    
    // Check if user has scrolled to near bottom (within 100px)
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      loadMoreTransactions();
    }
  }, [loadMoreTransactions]);

  // Fetch transactions when the modal becomes visible
  useEffect(() => {
    if (visible) {
      setTransactions([]);
      setNextCursor(null);
      setHasMore(true);
      fetchTransactions();
    } else {
      setTransactions([]);
      setSelectedTransaction(null);
      setNextCursor(null);
      setHasMore(true);
    }
  }, [visible, fetchTransactions]);

  // View transaction details
  const viewTransactionDetails = useCallback(async (id) => {
    try {
      setDetailLoading(true);
      const transaction = await getTransactionById(id);
      setSelectedTransaction(transaction);
      setDetailVisible(true);
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      message.error("Failed to load transaction details");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Handle status change (approve/decline)
  const handleStatusChange = useCallback(
    async (id, action) => {
      try {
        setStatusLoading(true);
        await updateTransactionStatus(id, action);

        // Update the transaction in the list
        setTransactions((prev) =>
          prev.map((tx) =>
            tx.id === id
              ? {
                  ...tx,
                  status: action === "approve" ? "approved" : "declined",
                }
              : tx
          )
        );

        // If the transaction is currently being viewed, update it
        if (selectedTransaction && selectedTransaction.id === id) {
          setSelectedTransaction((prev) => ({
            ...prev,
            status: action === "approve" ? "approved" : "declined",
          }));
        }

        message.success(
          `Transaction ${
            action === "approve" ? "approved" : "declined"
          } successfully`
        );
      } catch (error) {
        console.error(`Error ${action}ing transaction:`, error);
        message.error(`Failed to ${action} transaction`);
      } finally {
        setStatusLoading(false);
      }
    },
    [selectedTransaction]
  );

  // Handle decline transaction
  const handleDecline = async (id, reason) => {
    try {
      setStatusLoading(true);
      await updateTransactionStatus(id, "decline", reason);

      // Update the transaction in the list
      setTransactions((prev) =>
        prev.map((tx) => (tx.id === id ? { ...tx, status: "declined" } : tx))
      );

      // If the transaction is currently being viewed, update it
      if (selectedTransaction && selectedTransaction.id === id) {
        setSelectedTransaction((prev) => ({
          ...prev,
          status: "declined",
        }));
      }

      message.success("Transaction declined successfully");
    } catch (error) {
      console.error("Error declining transaction:", error);
      message.error("Failed to decline transaction");
    } finally {
      setStatusLoading(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusMap = {
      "pending-payment": "processing",
      "pending-approval": "warning",
      approved: "success",
      declined: "error",
      "pending-disburse": "processing",
      "payment-failed": "error",
      "disburse-failed": "error",
      completed: "success",
      "pending-refund": "warning",
      "refund-failed": "error",
      refunded: "default",
    };

    return statusMap[status] || "default";
  };

  // Format transaction type for display
  const formatTransactionType = (type) => {
    if (!type) return "";

    // Convert kebab-case to Title Case
    return type
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Table columns
  const columns = [
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      width: 150,
      render: (text) => <Text copyable>{text}</Text>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => formatTransactionType(type),
    },
    {
      title: "Amount",
      key: "amount",
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>
            {formatCurrency(record.source_amount, record.source_currency_code)}
          </Text>
          <Text type="secondary">
            {formatCurrency(record.dest_amount, record.dest_currency_code)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {formatTransactionType(status)}
        </Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "created",
      key: "created",
      width: 150,
      render: (timestamp) => formatDate(timestamp),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button type="link" onClick={() => viewTransactionDetails(record.id)}>
          View Details
        </Button>
      ),
    },
  ];

  const modalTitle = userId
    ? `Transactions for ${userName}`
    : `Transactions for Business`;

  return (
    <>
      <Modal
        title={modalTitle}
        open={visible}
        onCancel={onClose}
        width={1000}
        footer={null}
      >
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{ 
            maxHeight: '400px', 
            overflowY: 'auto',
            border: '1px solid #f0f0f0',
            borderRadius: '6px'
          }}
        >
          <Table
            dataSource={transactions}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 900 }}
          />
        </div>
      </Modal>

      {/* Transaction Detail Modal */}
      <Modal
        title="Transaction Details"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        width={800}
        footer={null}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ textAlign: "center", padding: "50px 0" }}>
            <LoadingOutlined style={{ fontSize: 24 }} />
          </div>
        ) : selectedTransaction ? (
          <div style={{ position: "relative" }}>
            <Badge.Ribbon
              text={formatTransactionType(selectedTransaction.status)}
              color={getStatusColor(selectedTransaction.status)}
              style={{ right: 0 }}
            >
              <Card>
                <Descriptions
                  title="Transaction Information"
                  column={1}
                  bordered
                >
                  <Descriptions.Item label="Transaction ID">
                    {selectedTransaction.id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reference">
                    {selectedTransaction.reference}
                  </Descriptions.Item>
                  <Descriptions.Item label="Type">
                    {formatTransactionType(selectedTransaction.type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Description">
                    {selectedTransaction.description || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Source Amount">
                    {formatCurrency(
                      selectedTransaction.source_amount,
                      selectedTransaction.source_currency_code
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Destination Amount">
                    {formatCurrency(
                      selectedTransaction.dest_amount,
                      selectedTransaction.dest_currency_code
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Charge Amount">
                    {formatCurrency(
                      selectedTransaction.charge_amount,
                      selectedTransaction.source_currency_code
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Type">
                    {formatTransactionType(selectedTransaction.payment_type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Disbursement Type">
                    {formatTransactionType(selectedTransaction.disburse_type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {formatDate(selectedTransaction.created)}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                <Descriptions
                  title="Exchange Rate Information"
                  column={1}
                  bordered
                >
                  <Descriptions.Item label="Exchange Rate">
                    {`1 ${selectedTransaction.dest_currency_code} = ${(1 / selectedTransaction.exchange_rate).toFixed(2)} ${selectedTransaction.source_currency_code}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Exchange Rate">
                    {`1 ${selectedTransaction.dest_currency_code} = ${(1 / selectedTransaction.total_exchange_rate).toFixed(2)} ${selectedTransaction.source_currency_code}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Spread Rate">
                    {selectedTransaction.spread_rate}
                  </Descriptions.Item>
                  <Descriptions.Item label="Spread Revenue">
                    {formatCurrency(
                      selectedTransaction.spread_revenue,
                      selectedTransaction.dest_currency_code
                    )}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                {selectedTransaction.fee && (
                  <>
                    <Descriptions title="Fees" column={1} bordered>
                      {Object.entries(selectedTransaction.fee).map(
                        ([key, value]) => (
                          <Descriptions.Item
                            key={key}
                            label={key
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(" ")}
                          >
                            {formatCurrency(
                              value,
                              selectedTransaction.source_currency_code
                            )}
                          </Descriptions.Item>
                        )
                      )}
                    </Descriptions>
                    <Divider />
                  </>
                )}

                <Descriptions title="User Information" column={1} bordered>
                  <Descriptions.Item label="User ID">
                    {selectedTransaction.user_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Business ID">
                    {selectedTransaction.profile_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Name">
                    {selectedTransaction.user_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {selectedTransaction.user_email}
                  </Descriptions.Item>
                </Descriptions>

                {selectedTransaction.disburse_recipient_id && (
                  <>
                    <Divider />
                    <Descriptions
                      title="Recipient Information"
                      column={1}
                      bordered
                    >
                      <Descriptions.Item label="Recipient ID">
                        {selectedTransaction.disburse_recipient_id}
                      </Descriptions.Item>
                      <Descriptions.Item label="Name">
                        {selectedTransaction.disburse_recipient_name || "N/A"}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}

                {selectedTransaction.status === "pending-approval" && (
                  <>
                    <Divider />
                    <Space style={{ marginTop: 16 }}>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() =>
                          handleStatusChange(selectedTransaction.id, "approve")
                        }
                        loading={statusLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => {
                          Modal.confirm({
                            title: "Decline Transaction",
                            content: (
                              <Form form={declineForm} layout="vertical">
                                <Form.Item
                                  label="Reason"
                                  name="declineReason"
                                  rules={[
                                    {
                                      required: true,
                                      message:
                                        "Please provide a reason for declining",
                                    },
                                  ]}
                                >
                                  <Input.TextArea rows={4} />
                                </Form.Item>
                              </Form>
                            ),
                            onOk: async (close) => {
                              try {
                                const values =
                                  await declineForm.validateFields();
                                await handleDecline(
                                  selectedTransaction.id,
                                  values.declineReason
                                );
                                close();
                              } catch (error) {
                                // Form validation failed
                                return false;
                              }
                            },
                          });
                        }}
                        loading={statusLoading}
                      >
                        Decline
                      </Button>
                    </Space>
                  </>
                )}
              </Card>
            </Badge.Ribbon>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "50px 0" }}>
            <Text disabled>Select a transaction to view details</Text>
          </div>
        )}
      </Modal>
    </>
  );
};

UserTransactionsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string,
  profileId: PropTypes.string,
  userName: PropTypes.string,
};

export default UserTransactionsModal;
